/**
 *  GestureProcessor.js
 *
 *  Processes the raw stream of incoming touch data, recognizes specific gestures, and outputs gesture events
 *  to downstream components.
 *
 */

import autobind from 'class-autobind';
import { findUpTagByClass } from '@/util/findUpTag.js'

 const MIN_DRAG = 10 // 20;
 const MIN_SPREAD = 20;



export class Processor {
    // zoomBy and panBy support temporary hard-wiring. Ultimately, this should emit "gesture events" that are picked up
    // by the toplevel controller in the workspace, and dispatched to appropriate locations
    //
    constructor(eventTarget) {
        autobind(this);
        this.state = this.__reset();

        this.eventTarget = eventTarget;
    }

    setTouch(id, detail, time, target) {
        // console.log(this.touches);
        // const prevTouches = clonePropsDeep(this.touches);

        let touch = this.touches[id]
        let delta = 0

        if (!touch) {
            touch = {
                id,
                target,
                time,
                start: detail,
            }
            delta = 1;
        }
        touch.current = detail

        this.touches[id] = touch

        // if mid-gesture, remove the first ghost touch 
        // if (this.type) {
        //     for (let t in this.touches) {
        //         if (this.touches[t].ghost) {
        //             delete this.touches[t];
        //             break;
        //         }
        //     }
        // }

        this.touchCount = Object.keys(this.touches).length;

        // console.log('post set', Object.keys(this.touches).length, this.type);

        // console.log ('set', id, touch);

        this.update(id, delta)
    }

    clearTouch(id, time) {
        // console.log('clearTouch', id, Object.keys(this.touches).length)
        // const prevTouches = clonePropsDeep(this.touches);

        let touch = this.touches[id];

        let delta = -1;

        //if (this.type && this.touchCount >= 2) {
        //   this.touches[id].ghost = true;  
        // } else {
        if (touch) {
            delete this.touches[id];
        } else {
            delta = 0;
        }
        this.touchCount = Object.keys(this.touches).length;

        // console.log('post clear', Object.keys(this.touches).length, this.type);

        if (touch) {
            this.clearedTouchTarget = touch.target;
        
            // console.log('t', delta, touch);

            this.update(id, delta);

            if (this.clearTouchTimeout) {
                clearTimeout(this.clearTouchTimeout);
                this.clearTouchTimeout = null;
            }
            
            this.clearTouchTimeout = setTimeout(() => {
                this.clearedTouchTarget = null;
                this.lastTap = null;

                this.clearTouchTimeout = null;
            }, 500)
        }
    }


    //// STATE TRANSITION FUNCTIONS ////

    __touched() {
        return "touched"
    }

    __tapped(modifier) {
        const isAppUI = findUpTagByClass(this.clearedTouchTarget, 'ui');

        if (isAppUI) {
            return;
        }

        console.log("TAPPED", isAppUI);
        
        let evt = {
            previous: this.lastTap,
            start: clonePropsDeep(this.start),
            current: clonePropsDeep(this.current),

            gestureStartTarget: this.clearedTouchTarget,

            touchCount: this.touchCount,
            modifier
        }

        // console.log('>>>', this.lastTap ? this.lastTap.gestureStartTarget.className : 'NA', evt.gestureStartTarget.className)

        if (this.lastTap && this.lastTap.gestureStartTarget == evt.gestureStartTarget) {
            // check time 
            this.emit("doubletap", evt)   
        }
        else {
            this.emit("tap", evt)   
        }

        this.lastTap = evt;

        return "complete"
    }

    __gestureStarted(gesture) {
        // console.log('__gestureStarted', gesture)
        const gestureStartTarget = this.touchCount == 1 ? (this.touches[Object.keys(this.touches)[0]]).target : undefined;

        const isAppUI = findUpTagByClass(gestureStartTarget, 'ui');

        if (isAppUI) {
            return;
        }

        this.previous = null;

        this.type = gesture;
        this.coreTouchCount = this.touchCount; 

        let evt = {
            start: clonePropsDeep(this.start),
            current: clonePropsDeep(this.current),
            gestureStartTarget,
            type: gesture,
            delta: computeDelta(this.start, this.current),
            touchCount: this.touchCount,
            coreTouchCount: this.coreTouchCount
        }

        // console.log('**', evt.delta.pos);

        this.emit(`gesture-started(${gesture})`, evt)
        return "gesture-active"
    }

    __gestureChanged(delta) {
        // console.log('__gestureChanged', delta, this.previious, this.current)
        const gestureStartTarget = this.coreTouchCount == 1 ? (this.touches[Object.keys(this.touches)[0]]).target : undefined;

        const isAppUI = findUpTagByClass(gestureStartTarget, 'ui');

        if (isAppUI) {
            return;
        }


        if (this.previous == null) {
            this.previous = clonePropsDeep(this.start);
        }

        // console.log('this.start.spread', this.start.spread, 'current', this.current.spread);

        if (this.start.spread == 0) {
            this.start.spread = this.current.spread;
        }

        // console.log('__gestureChg', this.coreTouchCount);

        let evt = {
            start: clonePropsDeep(this.start),
            current: clonePropsDeep(this.current),
            gestureStartTarget,
            type: this.type,
            delta: computeDelta(this.previous, this.current),
            touchCount: this.touchCount,
            coreTouchCount: this.coreTouchCount
        }

        if (delta == 1 || delta == -1) {
            evt.gestureStartScale = 0;
        }

        // console.log('**', evt.current.spread, evt.start.spread);

        // console.log('change', this.current.spread, this.start.spread)
        this.emit(`gesture-changed`, evt)

        this.previous = clonePropsDeep(this.current);
    }

    __gestureStopped() {
        return "gesture-ending"
    }

    __gestureEnd() {
        const isAppUI = findUpTagByClass(this.clearedTouchTarget, 'ui');

        if (isAppUI) {
            return;
        }

        // console.log('start', this.start);
        // console.log('prev', this.previous);
        let evt = {
            start: clonePropsDeep(this.start),
            current: clonePropsDeep(this.current),
            end: clonePropsDeep(this.previous),
            gestureStartTarget: this.clearedTouchTarget,
            // type: gesture,
            // delta: computeDelta(this.start, this.current),
            // touchCount: this.touchCount,
            // coreTouchCount: this.coreTouchCount
        };
        this.emit(`gesture-ended`, evt);
        return "complete"
    }

    __reset() {
        // console.log('RESET');
        this.type = null;

        this.start = {};
        this.current = null;
        this.previous = null;

        this.touches = {};
        this.touchCount = 0;
        this.coreTouchCount = 0;

        return "initial"
    }


    // update() {
    //     if (this.pendingUpdate) return
    //     this.pendingUpdate = requestAnimationFrame(() => this._update())
    // }

    emit(type, detail) {
        this.eventTarget.dispatchEvent(new CustomEvent(type, {detail}));
    }

    update(id, delta) {
        // console.log('update', id, delta);
        let {touches, touchCount, previous} = this

        
        let points = getCurrentPoints(touches);
        this.current = computeGestureGeometry(points);
        
        // let prevPoints = getCurrentPoints(prevTouches);

        let nonGhostPoints = getCurrentNonGhostPoints(touches);
        // let prevNonGhostPoints = getCurrentNonGhostPoints(touches);

        let nonGhostPointsLen = nonGhostPoints.length;
        // let prevNonGhostPointsLen = prevNonGhostPoints.length;
        


        let totalDistanceMoved = 0;
        let totalDistanceSpread = 0;

        if (delta == 0) {
            // 0 update to existing touches
            if (nonGhostPointsLen > 0) {
                if (this.start.pos == null) {
                    this.start.pos = { ...this.current.pos }
                }

                totalDistanceMoved = computeDistance(this.current.pos, this.start.pos);
                
                if (touchCount >= 2) {
                    if (this.start.spread == null) {
                        this.start.spread = this.current.spread
                    }
                    totalDistanceSpread = Math.abs(this.current.spread - this.start.spread)
                } else {
                    this.start.spread = 0
                }
            }
            // 
        } else if (touchCount > 0 &&
            (delta == 1 || delta == -1)) {
            // +/- touch
            this.start = clonePropsDeep(this.current);
            this.previous = clonePropsDeep(this.current);
        }


        // console.log('update gesture', this.start, this.previous);
        // console.log('nonGhostPointsLen', nonGhostPointsLen, 'total move', totalDistanceMoved, 'total spread', totalDistanceSpread)



        let nextState;

        //// STATE MACHINE ////
        do {
            // console.log(`STATE: ${this.state}`);
            // console.log('xxx ghost vs check', (nonGhostPointsLen == touchCount));

            nextState = null
            switch (this.state) {

                case "initial":
                    if (touchCount > 0) {
                        nextState = this.__touched()
                    }
                    break

                case "touched":
                    // console.log("TOUCHED", this.state);
                    if (touchCount === 0) {
                        nextState = this.__tapped()
                    } else if (totalDistanceMoved > MIN_DRAG || totalDistanceSpread > MIN_SPREAD) {
                        // console.log("DRAG", totalDistanceMoved, totalDistanceSpread);
                        console.log('[[[   DRAG   ]]]')
                        nextState = this.__gestureStarted("drag")
                    } else {
                        // console.log (totalDistanceMoved, MIN_DRAG, '  ||  ', totalDistanceSpread, MIN_SPREAD)
                    }
                    break

                case "gesture-active":
                    // console.log("ACTIVE");
                    if (touchCount === 0) {
                        nextState = this.__gestureStopped()
                    } else {
                        nextState = this.__gestureChanged(delta)
                    }
                    break;

                case "gesture-ending":
                    console.log("END");
                    if (touchCount === 0) {
                        nextState = this.__gestureEnd()
                    }
                    break;

                case "complete":
                    console.log("COMPLETE");
                    nextState = this.__reset()
                    break;

                default:
                    console.error("DEFAULT case: state = ", this.state)
                    nextState = this.__reset()
            }
            // console.log('nextstate', nextState)

            if (!nextState) break
            this.state = nextState

        } while (true)
    

    }

}


export class TouchDetail {
    constructor({id, x, y, time}) {
        this.id = id
        this.x = x
        this.y = y
        this.time = time
    }

    static fromBrowserTouchObject(touch) {
        return new TouchDetail({
            id: touch.identifier,
            x: touch.clientX,
            y: touch.clientY,
            time: touch.timeStamp
        })
    }

    static fromBrowserPointerEvent(pointer) {
        return new TouchDetail({
            id: pointer.pointerId,
            x: pointer.clientX,
            y: pointer.clientY,
            time: pointer.timeStamp
        })
    }
}



function applyThreshold(threshold, value) {
    if (Math.abs(value) > threshold)
        return value
    return 0
}

function computeDistance(a, b) {
    return Math.sqrt(Math.pow(b.x - a.x, 2) + Math.pow(b.y - a.y, 2))
}

function computeCentroid(points) {
    let centroid = {x: 0, y: 0}
    let count = 0
    for (let point of points) {
        if (!point) continue
        count += 1
        centroid.x += point.x
        centroid.y += point.y
    }
    centroid.x /= count
    centroid.y /= count
    return centroid
}

function computeDelta(a, b) {
    return {
        pos: {
            x: b.pos.x - a.pos.x,
            y: b.pos.y - a.pos.y
        },
        spread: b.spread - a.spread
    }
}

function getCurrentPoints(touches) {
    return Object.values(touches)
    .map(touch => touch.current)
}
function getCurrentNonGhostPoints(touches) {
    return Object.values(touches)
    .filter(touch => touch.ghost !== true)
    .map(touch => touch.current)
}


/**
 * Computes and returns the relevant geometries for the current touch points.
 *
 * pos: {point} - the centroid of all touch points
 * spread: number - average distance from the points to the centroid
 *
 * @returns {{pos: {x: number, y: number}, spread: number}}
 */
function computeGestureGeometry(points) {
    // TODO: if a point disappears after the gesture is "locked-in" we need to bake in an offset
    // that will prevent the "pos" from jumping around when a finger is lifted.
    // this is complicated further, because ideally the normal behavior will resume when a finger is added back

    // console.log('computeGestureGeometry', points)

    let pos = null;
    if (points.length > 0) {
        pos = computeCentroid(points)
    }
    let spread = 0;
    for (let point of points) {
        spread += Math.abs(computeDistance(pos, point))
    }
    spread /= points.length
    return { pos, spread }
}

function clonePropsDeep(obj) {
    return JSON.parse(JSON.stringify(obj))
}

function d(obj) {
    return JSON.stringify(obj, null, ' ')
}


export default {
    Processor,
    TouchDetail
}